import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import Common from '../../shared/Common';
import type { IRecurringOperation } from '../../../interfaces/IRecurringOperation';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { SortUpIcon, SortDownIcon } from '../../shared/icons/Svgs';

import './style.css';

export interface ITableProps {
  data: IRecurringOperation[];
  loading: boolean;
  fetchData: () => Promise<void>;
  fetchTrigger: number;
  setFetchTrigger: React.Dispatch<React.SetStateAction<number>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowId: number;
  setSelectedRowId: React.Dispatch<React.SetStateAction<number>>;
  selectedRecurringOperation: IRecurringOperation | null;
  setSelectedRecurringOperation: React.Dispatch<React.SetStateAction<IRecurringOperation | null>>;
}

function RecurringOperationsTable(props: ITableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns: ColumnDef<IRecurringOperation>[] = React.useMemo<ColumnDef<IRecurringOperation>[]>(
    () => [
      {
        accessorFn: (row) => (row.isIncome ? 'Przychód' : 'Rachunek'),
        id: 'type',
        header: () => <span>Typ</span>,
      },
      {
        accessorFn: (row) => row.location?.locationName,
        id: 'locationName',
        header: () => <span>Lokalizacja</span>,
      },
      {
        accessorFn: (row) => row.description,
        id: 'description',
        header: () => <span>Nazwa</span>,
      },
      {
        accessorFn: (row) => (row.isIncome ? row.incomeCategory?.incomeCategoryName : row.category?.categoryFullName),
        id: 'category',
        header: () => <span>Kategoria</span>,
      },
      {
        accessorFn: (row) =>
          `${Common.Utils.getCurrencyString(row.cost)}${row.vat ? ` (+${Math.round(row.vat * 100)}% VAT)` : ''}`,
        enableSorting: false,
        id: 'cost',
        header: () => <span>Kwota</span>,
      },
      {
        accessorFn: (row) => row.interval,
        id: 'intervalTime',
        header: () => <span>Interwał</span>,
      },
      {
        accessorFn: (row) => new Date(row.lastUpdate),
        id: 'lastUpdateDate',
        header: () => <span>Poprzednia aktualizacja</span>,
        cell: (info) => (
          <span>{Common.Utils.getDatetimeWithoutZoneConverter(info.row.original.lastUpdate?.toString())}</span>
        ),
      },
      {
        accessorFn: (row) => new Date(row.nextUpdate),
        id: 'nextUpdateDate',
        header: () => <span>Następna aktualizacja</span>,
        cell: (info) => (
          <span>
            {info.row.original.active
              ? Common.Utils.getDatetimeWithoutZoneConverter(info.row.original.nextUpdate?.toString())
              : ''}
          </span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: props.data,
    columns: columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { fetchData, fetchTrigger } = props;

  useEffect(() => {
    fetchData();
  }, [fetchData, fetchTrigger]);

  const onTableRowClick = (index: number, recurringOperation: IRecurringOperation) => {
    props.setSelectedRowId(index);
    props.setSelectedRecurringOperation(recurringOperation);
    props.setShowModal(true);
  };

  const getRecurringOperationsTable = (preficate: (operation: IRecurringOperation) => boolean) => (
    <div className='mt-1 pb-1 table-responsive-sm'>
      <table id='recurring-operations-table' className='table table-hover table-bordered'>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        style: header.column.getCanSort()
                          ? {
                              cursor: 'pointer',
                            }
                          : {},
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                        desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {props.loading
            ? Common.Ui.getSkeletonTableRows(6, 8, 35)
            : table
                .getRowModel()
                .rows.filter((row) => preficate(row.original))
                .map((row) => (
                  <tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    className={
                      (props.selectedRowId === row.index ? 'selected-row' : undefined) +
                      ' ' +
                      (!row.original.active ? ' inactive' : undefined)
                    }
                    onClick={() => {
                      onTableRowClick(row.index, row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Row className='mt-1 pb-1'>
        <h3>Operacje stałe</h3>
      </Row>
      <Row className='mt-1 pb-1'>
        <Col>
          <Button
            variant='success'
            onClick={() => {
              props.setSelectedRowId(-1);
              props.setSelectedRecurringOperation(null);
              props.setShowModal(true);
            }}
          >
            Dodaj operację
          </Button>
        </Col>
      </Row>

      <h4 className='pt-2'>Rachunki</h4>
      {!props.loading && props.data.filter((row) => !row.isIncome).length === 0 ? (
        <>Brak operacji cyklicznych rachunków</>
      ) : (
        getRecurringOperationsTable((operation) => !operation.isIncome)
      )}
      <h4 className='pt-2'>Przychody</h4>
      {!props.loading && props.data.filter((row) => row.isIncome).length === 0 ? (
        <>Brak operacji cyklicznych przychodów</>
      ) : (
        getRecurringOperationsTable((operation) => operation.isIncome)
      )}
    </>
  );
}

export default RecurringOperationsTable;
